import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';

export type ToastVariant = 'default' | 'success' | 'warning' | 'error' | 'ai';

export interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
}

interface SingleToastProps extends ToastItem {
  onDismiss: (id: string) => void;
}

const VARIANT_STYLES: Record<ToastVariant, { accentClass: string; iconKind: IconKind }> = {
  default: { accentClass: 'text-[var(--success)]', iconKind: 'check-circ' },
  success: { accentClass: 'text-[var(--success)]', iconKind: 'check-circ' },
  warning: { accentClass: 'text-[var(--terracotta)]', iconKind: 'alert' },
  error: { accentClass: 'text-[var(--terracotta)]', iconKind: 'alert' },
  ai: { accentClass: 'text-[var(--ai-3)]', iconKind: 'sparkle' },
};

function SingleToast({ id, title, description, variant = 'default', onDismiss }: SingleToastProps) {
  const { accentClass, iconKind } = VARIANT_STYLES[variant];
  return (
    <div
      role="status"
      aria-live="polite"
      className="elva-fade pointer-events-auto flex min-w-[280px] items-center gap-[13px] rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] py-[13px] pl-3.5 pr-[18px] shadow-[var(--sh-3)]"
    >
      <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--cream-0)]', accentClass)}>
        <Icon kind={iconKind} size={17} stroke={2} />
      </span>
      <div className="flex-1">
        <div className="text-[13.5px] font-semibold text-[var(--ink-0)]">{title}</div>
        {description && (
          <div className="mt-px text-xs text-[var(--ink-3)]">{description}</div>
        )}
      </div>
      <button
        type="button"
        onClick={() => { onDismiss(id); }}
        aria-label="Dismiss"
        className="flex shrink-0 text-[var(--ink-4)]"
      >
        <Icon kind="x" size={15} />
      </button>
    </div>
  );
}

/* ── Toast context ── */
interface ToastContextValue {
  toast: (item: Omit<ToastItem, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <Toaster>');
  return ctx;
}

/* ── Toaster provider + portal ── */
export interface ToasterProps {
  children: React.ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

const POSITION_STYLES: Record<NonNullable<ToasterProps['position']>, React.CSSProperties> = {
  'top-right':     { top: 16, right: 16, alignItems: 'flex-end' },
  'top-left':      { top: 16, left: 16, alignItems: 'flex-start' },
  'bottom-right':  { bottom: 16, right: 16, alignItems: 'flex-end' },
  'bottom-left':   { bottom: 16, left: 16, alignItems: 'flex-start' },
  'top-center':    { top: 16, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' },
  'bottom-center': { bottom: 16, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' },
};

export function Toaster({ children, position = 'bottom-right' }: ToasterProps) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = React.useCallback((item: Omit<ToastItem, 'id'>) => {
    const id = Math.random().toString(36).slice(2);
    const duration = item.duration ?? 4000;
    setToasts((prev) => [...prev, { ...item, id }]);
    if (duration > 0) setTimeout(() => { dismiss(id); }, duration);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <div
        aria-live="assertive"
        style={{
          position: 'fixed',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          pointerEvents: 'none',
          ...POSITION_STYLES[position],
        }}
      >
        {toasts.map((t) => (
          <SingleToast key={t.id} {...t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
